import { Link } from '@inertiajs/react'

type TargetTier = 'pro' | 'portfolio'

interface Props {
  variant: 'modal' | 'inline' | 'banner'
  targetTier?: TargetTier
  title?: string
  message?: string
  feature?: string
  onClose?: () => void
}

const TIER_CONFIG = {
  pro: {
    defaultTitle: 'Upgrade to DebtLens Pro',
    price: '$19/mo',
    features: [
      'Up to 15 loans',
      'True cost breakdown per loan',
      'Weekly obligation digest',
      'AI document parser — auto-fill from PDFs',
    ],
    ctaColor: 'bg-emerald-600 hover:bg-emerald-700',
  },
  portfolio: {
    defaultTitle: 'Upgrade to DebtLens Portfolio',
    price: '$39/mo',
    features: [
      'Up to 50 loans',
      'Everything in Pro',
      'Portfolio health report',
      'Multi-property organization',
      'Priority support badge',
    ],
    ctaColor: 'bg-indigo-600 hover:bg-indigo-700',
  },
} as const

function defaultMessage(tier: TargetTier, feature?: string): string {
  if (tier === 'portfolio') {
    return feature
      ? `${feature} is a Portfolio feature. Upgrade to unlock it along with up to 50 loans and portfolio analytics.`
      : 'Upgrade to Portfolio for up to 50 loans, portfolio health reports, and multi-property organization.'
  }
  return feature
    ? `${feature} is a Pro feature. Upgrade to unlock it along with up to 15 loans, true cost analysis, and weekly digests.`
    : 'Upgrade to Pro for up to 15 loans, true cost analysis per loan, weekly obligation digests, and more.'
}

export default function BorrowerUpgradePrompt({
  variant,
  targetTier = 'pro',
  title,
  message,
  feature,
  onClose,
}: Props) {
  const config = TIER_CONFIG[targetTier]
  const displayTitle = title || config.defaultTitle
  const displayMessage = message || defaultMessage(targetTier, feature)

  if (variant === 'banner') {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900">{displayTitle}</p>
              <p className="text-sm text-amber-700 mt-0.5">{displayMessage}</p>
            </div>
          </div>
          <Link
            href="/my-loans/billing"
            className={`shrink-0 ml-4 px-4 py-1.5 rounded-lg text-sm font-semibold text-white ${config.ctaColor} transition-colors`}
          >
            Upgrade
          </Link>
        </div>
      </div>
    )
  }

  if (variant === 'inline') {
    return (
      <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-emerald-50 to-white p-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">{displayTitle}</p>
            <p className="text-xs text-gray-500 mt-0.5">{displayMessage}</p>
          </div>
          <Link
            href="/my-loans/billing"
            className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold text-white ${config.ctaColor} transition-colors`}
          >
            Upgrade — {config.price}
          </Link>
        </div>
      </div>
    )
  }

  // Modal variant
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative min-h-full flex items-center justify-center py-8 px-4">
        <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl p-6 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
            </svg>
          </div>

          <h2 className="text-lg font-bold text-gray-900 mb-2">{displayTitle}</h2>
          <p className="text-sm text-gray-600 mb-6">{displayMessage}</p>

          <ul className="text-left space-y-2 mb-6">
            {config.features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                <svg className="w-4 h-4 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
                {f}
              </li>
            ))}
          </ul>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              Maybe later
            </button>
            <Link
              href="/my-loans/billing"
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold text-white ${config.ctaColor} transition-colors text-center`}
            >
              Upgrade — {config.price}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
